import React, { useEffect, useState, useRef } from "react";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import { MdExpandMore } from "react-icons/md";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Pagination from "../../Component/Pagination";
import API_URL from "../../Config";
// import "../Customer/customer.css";
// import "../PurchaseIndents/purchaseIndents.css";

const StockLedgerList = ({ isSidebarExpanded }) => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    type: "success",
  });
  const fetchTimeout = useRef(null);

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ open: true, msg: message, type });
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem("authKey");
      const res = await fetch(`${API_URL}/api/barrel/products`, {
        headers: { Authorization: token },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch products");
      }

      const responseData = await res.json();
      setProducts(responseData.products || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      showSnackbar("Failed to load products", "error");
    }
  };

  const fetchData = async (
    currentPage,
    perPage,
    searchTerm,
    from,
    to,
    productId,
  ) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const params = new URLSearchParams({
        page: currentPage + 1,
        per_page: perPage,
        ...(searchTerm && { search: searchTerm }),
        ...(from && { from_date: from }),
        ...(to && { to_date: to }),
        ...(productId && { product_id: productId }),
      });

      const res = await fetch(
        `${API_URL}/api/barrel/stock/ledger?${params.toString()}`,
        {
          headers: { Authorization: authKey },
        },
      );

      if (!res.ok) {
        throw new Error("Failed to fetch data");
      }

      const responseData = await res.json();
      setData(responseData.ledger || []);
      setTotalItems(responseData.total || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    setLoading(true);
    clearTimeout(fetchTimeout.current);

    fetchTimeout.current = setTimeout(() => {
      fetchData(page, itemsPerPage, search, fromDate, toDate, selectedProduct);
    }, 600);

    return () => clearTimeout(fetchTimeout.current);
  }, [search, fromDate, toDate, selectedProduct, page, itemsPerPage]);

  const handleClearFilters = () => {
    setFromDate("");
    setToDate("");
    setSearch("");
    setSelectedProduct("");
    setPage(0);
  };

  const handleRefresh = () => {
    handleClearFilters();
    fetchData(0, itemsPerPage, "", "", "", "");
  };

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(itemsPerPage);
    localStorage.setItem("currentPage", currentPage);
    localStorage.setItem("itemsPerPage", itemsPerPage);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  };

  const getTransactionTypeBadge = (type) => {
    const isInward = type === "INWARD";
    return (
      <span
        style={{
          backgroundColor: isInward ? "#28a74520" : "#dc354520",
          color: isInward ? "#28a745" : "#dc3545",
          padding: "4px 12px",
          borderRadius: "8px",
          fontSize: "12px",
          fontWeight: "600",
          display: "inline-block",
          border: `1px solid ${isInward ? "#28a74540" : "#dc354540"}`,
        }}
      >
        {type}
      </span>
    );
  };

  const getReferenceTypeBadge = (type) => {
    return (
      <span
        style={{
          backgroundColor: "#007bff20",
          color: "#007bff",
          padding: "4px 10px",
          borderRadius: "6px",
          fontSize: "11px",
          fontWeight: "600",
          display: "inline-block",
          border: "1px solid #007bff40",
        }}
      >
        {type}
      </span>
    );
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <div className="data-list-heading-content-left-side">
                <h3>Stock Ledger</h3>
              </div>

              <div className="data-list-heading-content-right-side">
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="filter-dropdown"
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                    minWidth: "200px",
                    flex: "1 1 200px",
                    marginLeft: "10px",
                  }}
                >
                  <option value="">All Products</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.product_code} - {product.product_name}
                    </option>
                  ))}
                </select>

                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="filter-dropdown"
                  placeholder="From Date"
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                    minWidth: "150px",
                    flex: "1 1 150px",
                    marginLeft: "10px",
                  }}
                />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="filter-dropdown"
                  placeholder="To Date"
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                    minWidth: "150px",
                    flex: "1 1 150px",
                    marginLeft: "10px",
                  }}
                />

                <div
                  className="search-bar-container"
                  style={{ marginLeft: "10px" }}
                >
                  <input
                    type="text"
                    placeholder="Search reference..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="search-bar"
                    style={{ marginLeft: "10px" }}
                  />
                  <IoSearchOutline className="search-icon" />
                </div>

                <button className="refresh-button" onClick={handleRefresh}>
                  <TbRefresh />
                </button>
              </div>
            </div>
          </div>
          <div className="header-separator"></div>

          {/* TABLE */}
          <div className="data-list-body">
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : data.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. NO.</th>
                        <th>TRANSACTION DATE</th>
                        <th>PRODUCT</th>
                        <th>TRANSACTION TYPE</th>
                        <th className="center-alignment">QUANTITY (KL)</th>
                        <th className="center-alignment">BARRELS</th>
                        <th className="center-alignment">BALANCE (KL)</th>
                        <th className="center-alignment">BALANCE BARRELS</th>
                        <th>REFERENCE TYPE</th>
                        <th>REFERENCE NUMBER</th>
                        <th>REMARKS</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {data.map((row, index) => (
                        <tr key={row.id || index} className="hoverable-row">
                          <td className="center-alignment">{index + 1}</td>
                          <td>{formatDateTime(row.transaction_date)}</td>
                          <td>
                            <div
                              style={{ fontWeight: "600", marginBottom: "2px" }}
                            >
                              {row.product_code}
                            </div>
                            <div style={{ fontSize: "12px", color: "#666" }}>
                              {row.product_name}
                            </div>
                          </td>
                          <td>
                            {getTransactionTypeBadge(row.transaction_type)}
                          </td>
                          <td
                            className="center-alignment"
                            style={{
                              fontWeight: "600",
                              color:
                                row.transaction_type === "INWARD"
                                  ? "#28a745"
                                  : "#dc3545",
                            }}
                          >
                            {parseFloat(row.quantity_kl).toFixed(2)}
                          </td>
                          <td
                            className="center-alignment"
                            style={{ fontWeight: "600" }}
                          >
                            {row.quantity_barrels}
                          </td>
                          <td
                            className="center-alignment"
                            style={{
                              fontWeight: "600",
                              color: "#007bff",
                            }}
                          >
                            {parseFloat(row.balance_kl).toFixed(2)}
                          </td>
                          <td
                            className="center-alignment"
                            style={{
                              fontWeight: "600",
                              color: "#007bff",
                            }}
                          >
                            {row.balance_barrels}
                          </td>
                          <td>{getReferenceTypeBadge(row.reference_type)}</td>
                          <td style={{ fontWeight: "500" }}>
                            {row.reference_number || "N/A"}
                          </td>
                          <td style={{ fontSize: "13px", color: "#666" }}>
                            {row.remarks || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">No Ledger Found</div>
                  </div>
                )}
              </div>
              <Pagination
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={handlePaginationChange}
              />
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
        >
          {snackbar.msg}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default StockLedgerList;
